import React from "react";
import { Card, Col, Progress, Row, Space, Typography, Tooltip } from "antd";
import {
  BarChartOutlined,
  RadarChartOutlined,
  TableOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import DashboardLayout from "../components/DashboardLayout";
import PageLoader from "../components/PageLoader";
import RadarChart from "../components/RadarChart";
import Stats from "../components/Stats";
import { dashboardPalette } from "../theme";
import useCurrentUserData from "../hooks/useCurrentUserData";
import {
  AVERAGE_SKILL_HELP,
  getAverageSkillPercent,
  getBestSkill,
  getSkillDisplayName,
  skillExpToPercent,
} from "../utils/skillUtils";
import { getSkillIcon } from "../utils/skillIcons";

const sectionCardStyle: React.CSSProperties = {
  borderRadius: 24,
  border: `1px solid ${dashboardPalette.panelBorder}`,
  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
};

const eyebrowStyle: React.CSSProperties = {
  color: dashboardPalette.headerBlue,
  textTransform: "uppercase",
  letterSpacing: 1.1,
};

const Skills: React.FC = () => {
  const { userData, isLoading } = useCurrentUserData();
  const userSkills = userData?.Skill ?? [];

  const averageProgress = getAverageSkillPercent(userSkills);
  const bestSkill = getBestSkill(userSkills);
  const bestSkillPercent = bestSkill
    ? skillExpToPercent(bestSkill.Progress)
    : 0;
  const bestSkillStyle = bestSkill ? getSkillIcon(bestSkill.Materia) : null;

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1440, margin: "0 auto", width: "100%" }}>
        {isLoading && !userData ? (
          <PageLoader minHeight={480} />
        ) : (
          <>
            <Card
              className="app-glass"
              style={sectionCardStyle}
              bodyStyle={{ padding: 24 }}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Typography.Text strong style={eyebrowStyle}>
                  Analítica
                </Typography.Text>
                <Typography.Title
                  level={2}
                  style={{ margin: 0, color: dashboardPalette.headerBlue }}
                >
                  Análisis de habilidades
                </Typography.Title>
                <Typography.Paragraph
                  style={{
                    margin: 0,
                    color: dashboardPalette.textDark,
                    maxWidth: 760,
                  }}
                >
                  Visualizá tu progreso, compará tu rendimiento y encontrá los
                  puntos fuertes de tu perfil.
                </Typography.Paragraph>
              </Space>
            </Card>

            <Card
              className="app-glass hero-panel"
              style={{ ...sectionCardStyle, marginTop: 16 }}
              bodyStyle={{ padding: 24 }}
            >
              <Row gutter={[24, 20]} align="middle">
                <Col xs={24} md={14}>
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    <Typography.Text strong style={eyebrowStyle}>
                      Tu skill más fuerte
                    </Typography.Text>
                    <Space align="center" size={16}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            bestSkillStyle?.color ?? "var(--dashboard-track)",
                          color: "#fff",
                          fontSize: 26,
                          flexShrink: 0,
                        }}
                      >
                        {bestSkillStyle?.icon}
                      </div>
                      <Typography.Title
                        level={2}
                        style={{
                          margin: 0,
                          color: dashboardPalette.headerBlue,
                        }}
                      >
                        {bestSkill
                          ? getSkillDisplayName(bestSkill.Materia)
                          : "Sin datos"}
                      </Typography.Title>
                    </Space>
                    <Progress
                      percent={bestSkillPercent}
                      strokeColor="var(--dashboard-teal)"
                      trailColor="var(--dashboard-track)"
                      strokeWidth={18}
                      format={(percent) => `${Number(percent ?? 0)}%`}
                    />
                  </Space>
                </Col>
                <Col xs={24} md={10}>
                  <div
                    style={{
                      borderRadius: 18,
                      padding: 20,
                      background:
                        "linear-gradient(135deg, rgba(247,197,11,0.16), rgba(255,255,255,0.7))",
                      border: `1px solid ${dashboardPalette.panelBorder}`,
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{ color: dashboardPalette.headerBlue }}
                    >
                      Promedio general{" "}
                      <Tooltip title={AVERAGE_SKILL_HELP}>
                        <InfoCircleOutlined style={{ fontSize: 12 }} />
                      </Tooltip>
                    </Typography.Text>
                    <Progress
                      percent={averageProgress}
                      strokeColor="#ff6ddd"
                      trailColor="var(--dashboard-track)"
                      strokeWidth={18}
                      format={(percent) => `${Number(percent ?? 0)}%`}
                    />
                  </div>
                </Col>
              </Row>
            </Card>

            <Card
              className="app-glass"
              style={{ ...sectionCardStyle, marginTop: 16 }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space size={8} align="center">
                  <TableOutlined
                    style={{ color: dashboardPalette.headerBlue }}
                  />
                  <Typography.Text strong style={eyebrowStyle}>
                    Lista
                  </Typography.Text>
                </Space>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, color: dashboardPalette.headerBlue }}
                >
                  Progreso por habilidad
                </Typography.Title>
                <Stats skills={userSkills} />
              </Space>
            </Card>

            <Card
              className="app-glass"
              style={{ ...sectionCardStyle, marginTop: 16 }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space size={8} align="center">
                  <BarChartOutlined
                    style={{ color: dashboardPalette.headerBlue }}
                  />
                  <Typography.Text strong style={eyebrowStyle}>
                    Promedio general
                  </Typography.Text>
                </Space>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, color: dashboardPalette.headerBlue }}
                >
                  Resumen
                </Typography.Title>

                <Row gutter={[12, 12]}>
                  <Col xs={24} md={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        height: "100%",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        Skills totales
                      </Typography.Text>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: dashboardPalette.teal }}
                      >
                        {userSkills.length}
                      </Typography.Title>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        height: "100%",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        Promedio general
                      </Typography.Text>
                      <Typography.Title
                        level={2}
                        style={{ margin: 0, color: "#ff6ddd" }}
                      >
                        {averageProgress}%
                      </Typography.Title>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: 16,
                        height: "100%",
                        background: "rgba(255,255,255,0.72)",
                        border: `1px solid ${dashboardPalette.panelBorder}`,
                      }}
                    >
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        Mejor skill
                      </Typography.Text>
                      <Typography.Title
                        level={4}
                        style={{
                          margin: "6px 0 0",
                          color: dashboardPalette.headerBlue,
                        }}
                      >
                        {bestSkill
                          ? getSkillDisplayName(bestSkill.Materia)
                          : "Sin datos"}
                      </Typography.Title>
                      <Typography.Text
                        strong
                        style={{ color: dashboardPalette.teal }}
                      >
                        {bestSkillPercent}%
                      </Typography.Text>
                    </div>
                  </Col>
                </Row>
              </Space>
            </Card>

            <Card
              className="app-glass"
              style={{ ...sectionCardStyle, marginTop: 16 }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space size={8} align="center">
                  <RadarChartOutlined
                    style={{ color: dashboardPalette.headerBlue }}
                  />
                  <Typography.Text strong style={eyebrowStyle}>
                    Radar
                  </Typography.Text>
                </Space>
                {userSkills.length ? (
                  <RadarChart skills={userSkills} />
                ) : (
                  <Typography.Text>No hay skills para mostrar.</Typography.Text>
                )}
              </Space>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Skills;
